/**
 * Audio triggers — slice 3 of the audio runtime (docs/design/18-audio.md).
 *
 * The ONE place that knows "game signal X → play sound Y". Mirrors the VFX
 * bridge (`abilities/cast-vfx.ts`): subscribes to the server `broadcast(...)`
 * bus the game already emits and turns each event into an `audio.play(...)`.
 * Everything else in the client stays audio-agnostic — a new sound is a
 * registry row + one line here.
 *
 * Position model: world SFX resolve the emitter's XZ (player render object →
 * else schema entity) and play spatially; the listener (camera target) is set
 * each frame by the manager. An unresolved position falls back to flat.
 */

import type { Room } from "colyseus.js";
import type { HelloState } from "@tribez/shared";
import {
  ABILITY_CAST_SOUND,
  CREATURE_FAMILY,
  gatherSoundForKind,
  isTreeKind,
} from "@tribez/shared";
import { ABILITIES } from "@tribez/shared";
import type { AudioManager } from "./audio-manager";

type Vec2 = { x: number; z: number };

export interface AudioTriggerOptions {
  readonly room: Room<HelloState>;
  readonly audio: AudioManager;
  /** Player render-object position by sessionId (smoothed) — main.ts wires
   *  `players.get(id)?.object.position`. */
  readonly getPlayerPos: (id: string) => Vec2 | undefined;
}

interface HitPayload {
  sourceId: string;
  targetId: string;
  amount: number;
  attackKind?: string;
  isCrit: boolean;
  hpAfter: number;
  /** Target's world XZ — lets the sound be placed (and distance-attenuated) even
   *  when the target isn't in the local fog-gated state, so off-screen combat is
   *  heard faintly rather than at flat full volume. */
  x?: number;
  z?: number;
}
interface PairPayload { sourceId: string; targetId: string; x?: number; z?: number }
interface CreatureSwingPayload { sourceId: string; targetId: string; weaponId: string }
interface CreatureDeathPayload { creatureId: string; killerId: string }
interface BuildingPayload { id: string; type: string; x: number; z: number }
interface GatherTickPayload { sessionId: string; kind: string; x: number; z: number; felled: boolean }
interface CastResolvedPayload { abilityId: string; casterId: string; x: number; z: number; targetUnitId?: string; at: number }
interface AbilityRefPayload { casterId: string; abilityId: string }

export interface AudioTriggersAPI {
  dispose(): void;
}

export function installAudioTriggers(opts: AudioTriggerOptions): AudioTriggersAPI {
  const { room, audio, getPlayerPos } = opts;
  const state = room.state;

  /** Emitter XZ: player object → creature → building → resource → undefined. */
  function resolvePos(id: string): Vec2 | undefined {
    const p = getPlayerPos(id);
    if (p) return { x: p.x, z: p.z };
    const c = state.creatures.get(id);
    if (c) return { x: c.x, z: c.z };
    const b = state.buildings.get(id);
    if (b) return { x: b.x, z: b.z };
    const r = state.resources.get(id);
    if (r) return { x: r.x, z: r.z };
    return undefined;
  }

  function familyOf(creatureId: string): string | undefined {
    const c = state.creatures.get(creatureId);
    if (!c) return undefined;
    return CREATURE_FAMILY[c.creatureType];
  }

  /** Target XZ from local state, else the position carried on the broadcast (set
   *  for combat events) so fog-gated hits still attenuate by distance. */
  function combatPos(msg: { targetId: string; x?: number; z?: number }): Vec2 | undefined {
    return resolvePos(msg.targetId)
      ?? (msg.x !== undefined && msg.z !== undefined ? { x: msg.x, z: msg.z } : undefined);
  }

  // --- Combat ---------------------------------------------------------------

  room.onMessage("hit", (msg: HitPayload) => {
    const pos = combatPos(msg);
    // Buildings get the chunky structural-impact sound; units get hit/crit.
    if (state.buildings.has(msg.targetId)) {
      audio.play("build.hit", { position: pos });
      return;
    }
    audio.play(msg.isCrit ? "combat.crit" : "combat.hit", { position: pos });
  });

  room.onMessage("blocked", (msg: PairPayload) => {
    audio.play("combat.block", { position: combatPos(msg) });
  });

  // --- Creatures (spatial; family-shared clips) -----------------------------

  room.onMessage("creature-swing", (msg: CreatureSwingPayload) => {
    const fam = familyOf(msg.sourceId);
    if (!fam) return;
    audio.play(`creature.${fam}.attack`, { position: resolvePos(msg.sourceId) });
  });

  room.onMessage("creature-death", (msg: CreatureDeathPayload) => {
    const fam = familyOf(msg.creatureId);
    // Resolve position BEFORE the schema entity is removed (death → delete).
    const pos = resolvePos(msg.creatureId);
    if (!fam) return;
    audio.play(`creature.${fam}.death`, { position: pos });
  });

  // --- Buildings ------------------------------------------------------------

  room.onMessage("building-placed", (msg: BuildingPayload) => {
    audio.play("build.place", { position: { x: msg.x, z: msg.z } });
  });

  room.onMessage("building-killed", (msg: BuildingPayload) => {
    audio.play("build.destroyed", { position: { x: msg.x, z: msg.z } });
  });

  // --- Gathering (per-swing; the lone new server emit) ----------------------

  room.onMessage("gather-tick", (msg: GatherTickPayload) => {
    const pos = { x: msg.x, z: msg.z };
    if (msg.felled && isTreeKind(msg.kind)) {
      audio.play("gather.tree-fall", { position: pos });
      return;
    }
    const sound = gatherSoundForKind(msg.kind);
    if (sound) audio.play(sound, { position: pos });
  });

  // --- Ability casts --------------------------------------------------------

  room.onMessage("castResolved", (msg: CastResolvedPayload) => {
    const sound = ABILITY_CAST_SOUND[msg.abilityId];
    if (!sound) return; // toggle/channel abilities → handled by their own events
    // Anchor at the resolved point (or caster, for self-casts the x/z is caster).
    const pos = resolvePos(msg.casterId) ?? { x: msg.x, z: msg.z };
    audio.play(sound, { position: pos });
  });

  room.onMessage("toggleStarted", (msg: AbilityRefPayload) => {
    audio.play("cast.toggle-on", { position: resolvePos(msg.casterId) });
  });
  room.onMessage("toggleStopped", (msg: AbilityRefPayload) => {
    audio.play("cast.toggle-off", { position: resolvePos(msg.casterId) });
  });

  // Channel: one looping bed while the channel runs. v1 keys the loop globally
  // (single local channeller is the common case); a second concurrent channel
  // would share the one loop instance — acceptable until it matters.
  room.onMessage("channelStarted", (msg: AbilityRefPayload) => {
    if (ABILITIES[msg.abilityId]) audio.loop("cast.channel", { fadeMs: 200 });
  });
  room.onMessage("channelStopped", () => {
    audio.stop("cast.channel", { fadeMs: 400 });
  });

  // --- UI -------------------------------------------------------------------

  // Unreachable move/harvest click (pathfinding pass 2026-07-02): pair the red
  // rejected-ring (main.ts) with the soft denied blip. UI bus, non-spatial —
  // it's feedback about the player's ORDER, not a world event at the click point.
  room.onMessage("move-rejected", () => {
    audio.play("ui.denied");
  });

  // No teardown to do — Colyseus drops message handlers when the room leaves.
  function dispose(): void { /* handlers die with the room */ }
  return { dispose };
}
